<template>
  <div class="ov-cal-wrapper" :class="{ 'ov-cal-wrapper--flat': flat }">

    <!-- Transparent overlay to close context menu on outside click -->
    <div v-if="openMenuId" class="ctx-overlay" @click.stop="closeMenu" />

    <!-- Month switcher -->
    <div class="ov-month-switcher">
      <button class="ov-month-nav" :class="{ 'ov-month-nav--team': isTeamMode }" @click="goPrevMonth">
        <v-icon size="20">mdi-chevron-left</v-icon>
      </button>
      <span class="ov-month-label">{{ monthTitle }}</span>
      <button class="ov-month-nav" :class="{ 'ov-month-nav--team': isTeamMode }" @click="goNextMonth">
        <v-icon size="20">mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- Grid -->
    <transition :name="monthSlideDir === 'right' ? 'ov-slide-right' : 'ov-slide-left'" mode="out-in">
    <div :key="calendarPageId" class="ov-cal-grid">
      <!-- Weekday headers -->
      <div v-for="(wd, i) in weekdayLabels" :key="'w' + i" class="ov-weekday">{{ wd }}</div>

      <!-- Day cells -->
      <div
        v-for="(cell, i) in calendarCells"
        :key="'c' + i"
        class="ov-day"
        :class="{
          'ov-day--today': cell.isToday,
          'ov-day--other': cell.otherMonth,
          'ov-day--past': cell.isPast && !cell.otherMonth,
          'ov-day--attending': cell.hasAttending && !cell.otherMonth,
          'ov-day--team': isTeamMode && !cell.otherMonth,
          'ov-day--team-single': isTeamMode && !cell.otherMonth && cell.teamChip && cell.events.length === 1,
        }"
        :style="isTeamMode && !cell.otherMonth && cell.teamChip && cell.events.length === 1
          ? { backgroundColor: cell.teamChip.bg, color: cell.teamChip.fg, border: '2px solid rgba(180,180,180,.3)' }
          : {}"
        @click="onDayCellClick(cell)"
      >
        <div class="ov-day-top" :class="{ 'ov-day-top--team': isTeamMode }">
          <span
            class="ov-day-number"
            :class="{ 'ov-day-number--team': isTeamMode }"
            :style="isTeamMode && !cell.otherMonth && cell.teamChip && cell.events.length === 1
              ? { color: cell.teamChip.fg }
              : {}"
          >
            {{ cell.day }}
          </span>
        </div>
        <div class="ov-day-bottom" :class="{ 'ov-day-bottom--team': isTeamMode }">
          <!-- Team mode: single event -->
          <template v-if="isTeamMode && !cell.otherMonth && cell.teamChip && cell.events.length === 1">
            <!-- COMPACT: shortcut + score, click → v-menu -->
            <v-menu
              v-if="isCompact"
              :model-value="openMenuId === cell.teamChip._id"
              @update:model-value="v => { if (!v) openMenuId = null }"
              :close-on-content-click="false"
              location="bottom"
              transition="fade-transition"
              :offset="[0, 8]"
            >
              <template #activator="{ props: menuProps }">
                <div
                  class="ov-team-compact cursor-pointer"
                  v-bind="menuProps"
                  @click.stop="openMenuFor(cell.teamChip!._id)"
                >
                  <div class="ov-team-shortcut">{{ getShotcut(cell.teamChip.opponentName) }}</div>
                  <div class="ov-team-time">{{ cell.teamChip.label }}</div>
                </div>
              </template>
              <div class="ctx-menu" @click.stop>
                <template v-if="cell.teamChip.hasVideo">
                  <div class="ctx-item ctx-item--bare">
                    <EventRecap
                      :simple="true"
                      :event="{
                        _id: cell.teamChip.raw._id,
                        eventVideo: (cell.teamChip.raw as any).eventVideo,
                        leagueImage: (cell.teamChip.raw as any)._league?.imageUrl,
                        title: (cell.teamChip.raw as any)._league?.title
                      }"
                      :is-league-admin="false"
                    />
                  </div>
                  <div class="ctx-divider" />
                </template>
                <div
                  class="ctx-item"
                  role="button"
                  tabindex="0"
                  @click.stop="router.push(getEventRoute(cell.teamChip!.raw)); closeMenu()"
                  @keydown.enter.stop="router.push(getEventRoute(cell.teamChip!.raw)); closeMenu()"
                >
                  <span class="href-text">{{ ['friendly-match','blue-white','training'].includes(cell.teamChip!.raw.type) ? $t('eventTile.detail') : $t('eventTile.gamecenter') }}</span>
                </div>
              </div>
            </v-menu>

            <!-- DESKTOP: logo + score + footer icons -->
            <template v-else>
              <div class="ov-team-single">
                <div class="ov-team-logo-wrapper cursor-pointer" @click.stop="router.push(getEventRoute(cell.teamChip.raw))">
                  <img
                    v-if="cell.teamChip.logo"
                    :src="getTeamImage(cell.teamChip.logo, 'w150')"
                    class="ov-team-logo"
                    alt=""
                  />
                </div>
                <div class="ov-team-label cursor-pointer" :style="{ color: cell.teamChip.color }" @click.stop="router.push(getEventRoute(cell.teamChip.raw))">
                  {{ cell.teamChip.label }}
                </div>
              </div>
              <!-- Footer: gamecenter + highlights -->
              <div class="ov-team-footer" :class="{ 'ov-team-footer--single': !cell.teamChip.hasVideo }" :style="{ color: cell.teamChip.color }">
                <button class="ov-team-action" @click.stop="router.push(getEventRoute(cell.teamChip.raw))" aria-label="Gamecenter">
                  <v-icon size="22">mdi-hockey-sticks</v-icon>
                </button>
                <span v-if="cell.teamChip.hasVideo" class="ov-team-divider">|</span>
                <button v-if="cell.teamChip.hasVideo" class="ov-team-action" aria-label="Highlights" @click.stop>
                  <EventRecap
                    :simple="true"
                    icon="mdi-play-circle-outline"
                    :color="cell.teamChip.color"
                    :event="{
                      _id: cell.teamChip.raw._id,
                      eventVideo: (cell.teamChip.raw as any).eventVideo,
                      leagueImage: (cell.teamChip.raw as any)._league?.imageUrl,
                      title: (cell.teamChip.raw as any)._league?.title
                    }"
                    :is-league-admin="false"
                  />
                </button>
              </div>
            </template>
          </template>

          <!-- Team mode: multiple events -->
          <template v-else-if="isTeamMode && !cell.otherMonth && cell.teamChips.length > 1">
            <div class="ov-team-multi">
              <div
                v-for="(chip, j) in cell.teamChips.slice(0, 3)"
                :key="j"
                class="ov-team-chip"
                :style="{ backgroundColor: chip.bg, color: chip.fg, border: '1px solid rgba(180,180,180,.3)' }"
              >
                <span class="ov-team-chip-text">{{ getShotcut(chip.opponentName) }}</span>
              </div>
              <span v-if="cell.events.length > 3" class="ov-team-more">+{{ cell.events.length - 3 }}</span>
            </div>
          </template>

          <!-- Normal mode: dots -->
          <div v-else-if="!cell.otherMonth && cell.events.length > 0" class="ov-day-dots" :class="{ 'ov-day-dots--past': cell.isPast }">
            <span
              v-for="(ev, j) in cell.events.slice(0, 4)"
              :key="j"
              class="ov-dot"
              :style="{ backgroundColor: getEventColor(ev) }"
            />
            <span v-if="cell.events.length > 4" class="ov-dot-more">+{{ cell.events.length - 4 }}</span>
          </div>
        </div>
      </div>
    </div>
    </transition>

    <!-- Academy legend -->
    <div v-if="isAcademyMode && !hideLegend" class="ov-cal-legend">
      <div
        class="ov-legend-item ov-legend-item--clickable"
        :class="{ 'ov-legend-item--hidden': hiddenFilters.has('training') }"
        @click="toggleFilter('training')"
      >
        <span class="ov-legend-swatch" :style="{ backgroundColor: calendarColors('red') }" />
        <span>{{ $t('calendarLegend.training') }}</span>
      </div>
      <div
        class="ov-legend-item ov-legend-item--clickable"
        :class="{ 'ov-legend-item--hidden': hiddenFilters.has('blue-white') }"
        @click="toggleFilter('blue-white')"
      >
        <span class="ov-legend-swatch" :style="{ backgroundColor: calendarColors('blue') }" />
        <span>{{ $t('calendarLegend.teamGame') }}</span>
      </div>
      <div
        class="ov-legend-item ov-legend-item--clickable"
        :class="{ 'ov-legend-item--hidden': hiddenFilters.has('friendly-match') }"
        @click="toggleFilter('friendly-match')"
      >
        <span class="ov-legend-swatch" :style="{ backgroundColor: calendarColors('green') }" />
        <span>{{ $t('calendarLegend.friendly') }}</span>
      </div>
    </div>

    <!-- Normal legend -->
    <div v-else-if="!hideLegend" class="ov-cal-legend">
      <div
        class="ov-legend-item ov-legend-item--clickable"
        :class="{ 'ov-legend-item--hidden': hiddenFilters.has('blue-white') }"
        @click="toggleFilter('blue-white')"
      >
        <span class="ov-legend-swatch" :style="{ backgroundColor: calendarColors('blue') }" />
        <span>{{ $t('calendarLegend.teamGame') }}</span>
      </div>
      <div
        class="ov-legend-item ov-legend-item--clickable"
        :class="{ 'ov-legend-item--hidden': hiddenFilters.has('friendly-match') }"
        @click="toggleFilter('friendly-match')"
      >
        <span class="ov-legend-swatch" :style="{ backgroundColor: calendarColors('green') }" />
        <span>{{ $t('calendarLegend.friendly') }}</span>
      </div>
      <div
        v-for="league in watchedLeagues"
        :key="league._id"
        class="ov-legend-item ov-legend-item--clickable"
        :class="{ 'ov-legend-item--hidden': hiddenFilters.has(`league:${league._id}`) }"
        @click="toggleFilter(`league:${league._id}`)"
      >
        <span class="ov-legend-swatch" :style="{ backgroundColor: calendarColors(league.color) }" />
        <span>{{ league.title }}</span>
      </div>
    </div>
  </div>

  <!-- Day detail modal -->
  <v-dialog v-model="showModal" max-width="500" class="backdrop">
    <div class="day-modal">
      <!-- Header -->
      <div class="day-modal-header">
        <button class="day-modal-btn" @click="showModal = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
        <div class="day-modal-title">
          {{ formattedDate }}
        </div>
        <button v-if="canAddEventAllowed" class="day-modal-btn day-modal-btn--add" @click="onPlusIconClick">
          <v-icon size="20">mdi-plus</v-icon>
        </button>
        <div v-else class="day-modal-btn-placeholder"></div>
      </div>

      <!-- Content -->
      <div class="day-modal-content">
        <div v-if="filteredEvents.length" class="section-content ma-4" :class="{ 'section-content--menu-active': menuOpenCount > 0 }">
          <template v-for="(event, index) in filteredEvents" :key="index">
            <div class="day-modal-divider" v-if="index > 0"></div>
            <component
              :is="getEventTileComponent(event.type)"
              :event="event"
              :showEdit="true"
              :showMenu="true"
              :fullDate="true"
              :showTeamLogos="true"
              :showLeagueHeader="isLeagueEvent(event)"
              @navigate="showModal = false"
              @deleteEvent="handleDeleteEvent"
              @cancelEvent="handleCancelEvent"
              @editEvent="handleEditEvent"
              @menuOpen="menuOpenCount++"
              @menuClose="menuOpenCount--"
            />
          </template>
        </div>
        <div v-else class="day-modal-empty">
          Žádné události pro tento den.
        </div>
      </div>
    </div>
  </v-dialog>

  <EventDeleteWarningModal
    v-model:show="showDeleteDialog"
    :event="eventToDelete"
    @confirm-delete="handleDelete"
  />

  <EventFormModal
    v-model="showAddEventDialog"
    :eventToEdit="editedEvent"
    :initialDate="selectedDate"
    :presetLeagueId="presetLeagueId"
    :presetSeasonId="presetSeasonId"
    @saved="handleNewEvent"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, watchEffect } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import axiosInstance from '@/services/axiosInstance'
import { calendarColors } from '@/utils/colorUtils'
import { canAddEvents } from '@/utils/userUtils'
import { getTeamImage } from '@/utils/imageUtils'
import { getShotcut } from '@/utils/teamUtils'
import { useEventTileResolver } from '@/composables/useEventTileResolver'
import { useUserAttending } from '@/composables/useUserAttending'
import { useUserStore } from '@/stores/user/userStore'
import { useAppStore } from '@/stores/app/appStore'
import EventFormModal from '@/components/EventFormModal.vue'
import EventDeleteWarningModal from '@/components/EventDeleteWarningModal.vue'
import EventRecap from '@/components/EventRecap.vue'
import type { IEvent, ILeague } from '@/types'

/* ── composables ── */
const { getEventTileComponent } = useEventTileResolver()
const { isAttending } = useUserAttending()
const router = useRouter()
const route = useRoute()
const { t, locale: i18nLocale } = useI18n()

const localeToIntl: Record<string, string> = {
  cs: 'cs-CZ', sk: 'sk-SK', en: 'en-US', de: 'de-DE',
  'de-ch': 'de-CH', pl: 'pl-PL', fi: 'fi-FI', sv: 'sv-SE', da: 'da-DK',
}
const userStore = useUserStore()
const appStore = useAppStore()
const { smAndDown } = useDisplay()
const isAcademyMode = computed(() => !!props.academyUrlTitle)
const isTeamMode = computed(() => !!props.teamId && !isAcademyMode.value)
const isCompact = computed(() => smAndDown.value)

/* ── team compact menu state ── */
const openMenuId = ref<string | null>(null)
const openMenuFor = (id: string) => (openMenuId.value = id)
const closeMenu = () => (openMenuId.value = null)

/* ── helpers ── */
const isLeagueEvent = (event: IEvent) => {
  const t = String(event.type ?? '')
  return t !== 'friendly-match' && t !== 'blue-white' && t !== 'training' && t !== 'camp' && t !== 'beer'
}

const getEventRoute = (event: IEvent) => {
  if (isLeagueEvent(event)) return `/gamecenter/${event._id}`
  return `/event/${event._id}`
}

/* ── emits ── */
const emit = defineEmits<{
  (e: 'monthChange', title: string): void
  (e: 'month-data', data: { year: number; month: number }): void
}>()

/* ── props ── */
const props = withDefaults(defineProps<{
  flat?: boolean
  hideLegend?: boolean
  league?: string | null
  teamId?: string | null
  academyUrlTitle?: string | null
  allWatched?: boolean
  strictLeague?: boolean
  initialYear?: number | null
  initialMonth?: number | null
  presetLeagueId?: string | null
  presetSeasonId?: string | null
}>(), {
  flat: false,
  hideLegend: false,
  league: null,
  allWatched: true,
  strictLeague: false,
  teamId: null,
  academyUrlTitle: null,
  initialYear: null,
  presetLeagueId: null,
  presetSeasonId: null,
  initialMonth: null,
})

/* ── state ── */
const currentYear = ref(props.initialYear ?? new Date().getFullYear())
const currentMonth = ref(props.initialMonth ?? new Date().getMonth()) // 0-based
const monthEvents = ref<IEvent[]>([])
const filteredEvents = ref<IEvent[]>([])
const leagues = ref<ILeague[]>([])
const selectedLeague = ref<any | null>(null)
const selectedDate = ref<Date | null>(null)
const showModal = ref(false)
const showDeleteDialog = ref(false)
const eventToDelete = ref<IEvent | null>(null)
const showAddEventDialog = ref(false)
const editedEvent = ref<IEvent | null>(null)
const canAddEventAllowed = ref(false)
const now = ref(new Date())
const menuOpenCount = ref(0)
const hiddenFilters = ref(new Set<string>())

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

const leagueUrlTitle = computed(() => {
  const id = route.params.id
  return typeof id === 'string' ? id : (Array.isArray(id) ? id[0] : null)
})

/* ── calendarPageId (YYYY-MM) ── */
const calendarPageId = computed(() =>
  `${currentYear.value}-${String(currentMonth.value + 1).padStart(2, '0')}`
)

/* ── date / grid helpers ── */
const locale = computed(() => localeToIntl[i18nLocale.value] || 'cs-CZ')

const monthTitle = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value, { month: 'long', year: 'numeric' })
  const d = new Date(currentYear.value, currentMonth.value, 1)
  const t = fmt.format(d)
  return t.charAt(0).toUpperCase() + t.slice(1)
})

const weekdayLabels = computed(() => {
  const base = new Date(2023, 4, 1) // Monday
  const fmt = new Intl.DateTimeFormat(locale.value, {
    weekday: 'short',
  })
  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(base)
    d.setDate(base.getDate() + i)
    return fmt.format(d).replace(/^\p{Ll}/u, c => c.toUpperCase())
  })
})

const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate()
const firstDayOffset = (y: number, m: number) => (new Date(y, m, 1).getDay() + 6) % 7
const resetTime = (date: Date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/* ── events by day ── */
type DayCell = {
  day: number
  otherMonth: boolean
  isToday: boolean
  isPast: boolean
  hasAttending: boolean
  events: IEvent[]
  teamChip: TeamDayChip | null
  teamChips: TeamDayChip[]
}

const eventsByDay = computed<Map<number, IEvent[]>>(() => {
  const map = new Map<number, IEvent[]>()
  const hidden = hiddenFilters.value
  for (const ev of monthEvents.value) {
    if (hidden.size > 0 && hidden.has(getEventFilterKey(ev))) continue

    const dateFrom = resetTime(new Date(ev.dateFrom as any))
    const dateTo = ev.dateTo ? resetTime(new Date(ev.dateTo as any)) : dateFrom

    // span multi-day events across all days
    const cur = new Date(dateFrom)
    while (cur <= dateTo) {
      if (cur.getFullYear() === currentYear.value && cur.getMonth() === currentMonth.value) {
        const day = cur.getDate()
        if (!map.has(day)) map.set(day, [])
        map.get(day)!.push(ev)
      }
      cur.setDate(cur.getDate() + 1)
    }
  }
  return map
})

const teamChipsByDay = computed<Map<number, TeamDayChip[]>>(() => {
  if (!isTeamMode.value) return new Map()
  const map = new Map<number, TeamDayChip[]>()
  for (const [day, events] of eventsByDay.value.entries()) {
    map.set(day, events.map(ev => buildTeamChip(ev)))
  }
  return map
})

const calendarCells = computed<DayCell[]>(() => {
  const y = currentYear.value
  const m = currentMonth.value
  const totalDays = daysInMonth(y, m)
  const startOff = firstDayOffset(y, m)
  const today = new Date()

  const prevM = m === 0 ? 11 : m - 1
  const prevY = m === 0 ? y - 1 : y
  const prevDays = daysInMonth(prevY, prevM)

  const cells: DayCell[] = []

  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  // leading days from prev month
  for (let i = startOff - 1; i >= 0; i--) {
    const cellDate = new Date(prevY, prevM, prevDays - i)
    cells.push({ day: prevDays - i, otherMonth: true, isToday: false, isPast: cellDate < todayMidnight, hasAttending: false, events: [], teamChip: null, teamChips: [] })
  }

  // current month
  for (let d = 1; d <= totalDays; d++) {
    const isToday = today.getFullYear() === y && today.getMonth() === m && today.getDate() === d
    const cellDate = new Date(y, m, d)
    const dayEvents = eventsByDay.value.get(d) || []
    const hasAttending = dayEvents.some(ev => isAttending(ev))
    const chips = teamChipsByDay.value.get(d) || []
    cells.push({ day: d, otherMonth: false, isToday, isPast: cellDate < todayMidnight, hasAttending, events: dayEvents, teamChip: chips[0] || null, teamChips: chips })
  }

  // trailing days from next month — always fill to 42 cells (6 rows × 7)
  const totalCells = 42
  const nextM = m === 11 ? 0 : m + 1
  const nextY = m === 11 ? y + 1 : y
  for (let i = 1; cells.length < totalCells; i++) {
    const cellDate = new Date(nextY, nextM, i)
    cells.push({ day: i, otherMonth: true, isToday: false, isPast: cellDate < todayMidnight, hasAttending: false, events: [], teamChip: null, teamChips: [] })
  }

  return cells
})

/* ── event filter key ── */
function getEventFilterKey(ev: IEvent): string {
  // Academy mode: filter by event type
  if (isAcademyMode.value) {
    switch (ev.type) {
      case 'training': case 'camp': return 'training'
      case 'blue-white': return 'blue-white'
      case 'friendly-match': return 'friendly-match'
      default: return 'other'
    }
  }
  // Pokud má _league, vždy spadá pod ligu (i přáteláky/týmové hry repre týmu)
  if (ev._league?._id) return `league:${ev._league._id}`
  switch (ev.type) {
    case 'blue-white': return 'blue-white'
    case 'friendly-match': return 'friendly-match'
    default:
      return 'other'
  }
}

function toggleFilter(key: string) {
  const s = hiddenFilters.value
  if (s.has(key)) s.delete(key)
  else s.add(key)
  // trigger reactivity
  hiddenFilters.value = new Set(s)
}

/* ── event color mapping ── */
const getLeagueColor = (ev: IEvent) => ev._league?.color || 'teal'

function getEventColor(ev: IEvent): string {
  // Academy mode: always use per-type colors
  // Normal mode: use league color if available
  if (!isAcademyMode.value && ev._league?.color) return calendarColors(ev._league.color)
  switch (ev.type) {
    case 'training':
    case 'camp':
      return calendarColors('red')
    case 'blue-white':
      return calendarColors('blue')
    case 'friendly-match':
      return calendarColors('green')
    case 'beer':
      return calendarColors('orange')
    case 'superliga':
      return calendarColors('purple')
    default:
      return calendarColors(getLeagueColor(ev))
  }
}

function hexToRgb(hex: string) {
  const h = hex.replace('#', '')
  if (h.length === 3) return { r: parseInt(h[0]+h[0],16), g: parseInt(h[1]+h[1],16), b: parseInt(h[2]+h[2],16) }
  if (h.length === 6) return { r: parseInt(h.slice(0,2),16), g: parseInt(h.slice(2,4),16), b: parseInt(h.slice(4,6),16) }
  return { r: 255, g: 255, b: 255 }
}

function idealTextColor(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex)
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return L > 0.6 ? '#111111' : '#ffffff'
}

/* ── team mode color helpers ── */
const tokenPrimaryHex: Record<string, string> = {
  'white-blue': '#ffffff',
  'blue-white': '#1e40af',
  'white-black': '#ffffff',
  'black-gold': '#111111',
  'black-red': '#000000',
  'white-red': '#ffffff',
  'gold-blue': '#c5a100',
  'red-black': '#c1121f',
  'white-gold': '#ffffff',
}

function resolvePrimaryColor(team: any): string {
  const direct = team?.primaryTeamColor || team?.primaryColor || team?.color || ''
  if (typeof direct === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(direct)) return direct
  const token = Array.isArray(team?.colors) ? team.colors[0] : ''
  if (token && tokenPrimaryHex[token]) return tokenPrimaryHex[token]
  return '#e5e7eb'
}

function luminance({ r, g, b }: { r: number; g: number; b: number }) {
  const a = [r, g, b].map(v => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]
}

function getContrastRatio(hex1: string, hex2: string) {
  const L1 = luminance(hexToRgb(hex1))
  const L2 = luminance(hexToRgb(hex2))
  const brightest = Math.max(L1, L2)
  const darkest = Math.min(L1, L2)
  return (brightest + 0.05) / (darkest + 0.05)
}

function resolveSecondaryColor(team: any): string {
  const primary = resolvePrimaryColor(team)
  const direct = team?.secondaryTeamColor || team?.secondaryColor || team?.color || ''
  let secondary = ''
  if (typeof direct === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(direct)) secondary = direct
  if (!secondary) {
    const token = Array.isArray(team?.colors) ? team.colors[1] || team.colors[0] : ''
    if (token && tokenPrimaryHex[token]) secondary = tokenPrimaryHex[token]
  }
  if (!secondary) secondary = '#e5e7eb'
  const contrast = getContrastRatio(primary, secondary)
  if (contrast < 2) {
    const L = luminance(hexToRgb(primary))
    secondary = L > 0.5 ? '#000000' : '#ffffff'
  }
  return secondary
}

/* ── team chip types ── */
type TeamDayChip = {
  _id: string
  opponentName: string
  label: string
  logo: string
  bg: string
  fg: string
  color: string
  raw: IEvent
  hasVideo: boolean
}

function buildTeamChip(ev: IEvent): TeamDayChip {
  const teamIdVal = props.teamId
  let home = (ev as any)?.meta?.homeTeam || null
  let away = (ev as any)?.meta?.awayTeam || null
  let isHome = !!(home && String(home._id) === String(teamIdVal))
  let isAway = !!(away && String(away._id) === String(teamIdVal))

  if ((ev as any)?._team) {
    home = (ev as any)._team
    away = (ev as any)._team
    isHome = true
  }

  const bg = isHome ? resolvePrimaryColor(home) : isAway ? '#ffffff' : (ev as any)?._league?.color || '#f1f5f9'
  const color = isHome ? resolveSecondaryColor(home) : isAway ? '#000000' : (ev as any)?._league?.color || '#f1f5f9'
  const fg = idealTextColor(bg)
  const logo = isHome ? (away?.imageUrl || '') : isAway ? (home?.imageUrl || '') : (home?.imageUrl || away?.imageUrl || '')
  const time = (ev as any)?.timeFrom || ''

  let label = time
  const gs = (ev as any)?.gameStatus
  if (gs?.matchCompleted === true && Number.isFinite(gs?.home?.goals) && Number.isFinite(gs?.away?.goals)) {
    const homeGoals = gs.home.goals ?? 0
    const awayGoals = gs.away.goals ?? 0
    const ourGoals = isHome ? homeGoals : isAway ? awayGoals : null
    const oppGoals = isHome ? awayGoals : isAway ? homeGoals : null
    if (ourGoals !== null && oppGoals !== null) {
      let prefix: 'V' | 'P' | 'R' = 'R'
      if (ourGoals > oppGoals) prefix = 'V'
      else if (ourGoals < oppGoals) prefix = 'P'
      label = `${prefix} ${ourGoals}:${oppGoals}`
    }
  }

  const rawOpponentName = isHome ? (away?.name || '??') : isAway ? (home?.name || '??') : '??'
  let opponentName = rawOpponentName
  if (ev.type === 'blue-white' && rawOpponentName.includes('_')) {
    const part = rawOpponentName.split('_').slice(1).join('_')
    opponentName = t(part.toLowerCase(), part)
  }

  return {
    _id: String(ev._id ?? ''),
    opponentName,
    label,
    logo,
    bg,
    fg,
    color,
    raw: ev,
    hasVideo: !!(ev as any)?.eventVideo,
  }
}

/* ── navigation ── */
const monthSlideDir = ref<'left' | 'right'>('right')

function goPrevMonth() {
  monthSlideDir.value = 'left'
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
}

function goNextMonth() {
  monthSlideDir.value = 'right'
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
}

/* ── day click → modal ── */
function onDayCellClick(cell: DayCell) {
  if (cell.otherMonth) return
  selectedDate.value = new Date(currentYear.value, currentMonth.value, cell.day)
  filterDayEvents()
}

const parseTime = (time: string): number => {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

function filterDayEvents() {
  filteredEvents.value = []
  if (!selectedDate.value) return

  const selDay = resetTime(selectedDate.value).getTime()
  const evs = monthEvents.value.filter(ev => {
    const from = resetTime(new Date(ev.dateFrom as any)).getTime()
    const to = ev.dateTo ? resetTime(new Date(ev.dateTo as any)).getTime() : from
    return selDay >= from && selDay <= to
  })

  evs.sort((a, b) => {
    const tA = a.timeFrom ? parseTime(a.timeFrom) : -Infinity
    const tB = b.timeFrom ? parseTime(b.timeFrom) : -Infinity
    return tA - tB
  })

  filteredEvents.value = evs
  showModal.value = true
}

/* ── formatted date for modal title ── */
const formattedDate = computed(() => {
  if (!selectedDate.value) return ''
  return selectedDate.value.toLocaleDateString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
})

/* ── add / edit / delete events ── */
function onPlusIconClick() {
  editedEvent.value = null
  showModal.value = false
  showAddEventDialog.value = true
}

async function handleNewEvent() {
  await fetchLeagues()
  await fetchMonthEvents()
  filterDayEvents()
  appStore.triggerRefetch()
}

function handleDeleteEvent(event: IEvent) {
  eventToDelete.value = event
  if (event._id) showDeleteDialog.value = true
}

async function handleDelete() {
  if (!eventToDelete.value?._id) return
  try {
    await axiosInstance.delete(`/events/${eventToDelete.value._id}`)
    await fetchMonthEvents()
    filterDayEvents()
    appStore.triggerRefetch()
  } catch (e) {
    console.error('Chyba při rušení události:', e)
  }
}

function handleCancelEvent(event: IEvent) {
  handleDeleteEvent(event)
}

async function handleEditEvent(event: IEvent) {
  showModal.value = false
  try {
    const { data } = await axiosInstance.get(`/events/${event._id}`)
    editedEvent.value = data
  } catch (e) {
    console.error('Chyba při načítání události:', e)
    editedEvent.value = { ...event }
  }
  showAddEventDialog.value = true
}

/* ── fetch data ── */
async function fetchMonthEvents() {
  monthEvents.value = []
  try {
    let data: any
    if (isAcademyMode.value) {
      // Academy mode: fetch from academy endpoint with month range
      const y = currentYear.value
      const m = currentMonth.value
      const start = new Date(y, m, 1).toISOString()
      const end = new Date(y, m + 1, 0, 23, 59, 59, 999).toISOString()
      const res = await axiosInstance.get(
        `/events/scheduled-academy/${props.academyUrlTitle}`,
        { params: { start, end } }
      )
      data = res.data?.events ?? []
    } else if (props.teamId) {
      // Team mode: dedicated endpoint
      const res = await axiosInstance.get(
        `/events/monthbyteam/${calendarPageId.value}/${props.teamId}`
      )
      data = Array.isArray(res.data) ? res.data : res.data?.events ?? []
    } else {
      const res = await axiosInstance.get(
        `/events/month/${calendarPageId.value}/${props.league || ''}`,
        { params: { allwatched: props.allWatched, strictLeague: props.strictLeague, teamId: props.teamId } }
      )
      data = res.data
    }
    monthEvents.value = data
  } catch (e) {
    console.error('Chyba při načítání událostí:', e)
    if (!props.teamId) {
      await userStore.clearUser()
      window.location.href = '/login'
    }
  }
}

async function fetchLeagues() {
  try {
    const { data } = await axiosInstance.get<ILeague[]>(`${API_BASE_URL}/leagues`, { params: { limit: 100 } })
    leagues.value = data
  } catch (e) {
    console.error('Chyba při načítání lig:', e)
    leagues.value = []
  }
}

function pickSelectedLeague() {
  const lut = leagueUrlTitle.value
  if (!lut || !Array.isArray(leagues.value)) { selectedLeague.value = null; return }
  selectedLeague.value =
    leagues.value.find(l => l?.urlTitle === lut)
    ?? leagues.value.find(l => (l as any)?._league?.urlTitle === lut)
    ?? null
}

/* ── watched leagues for legend ── */
const watchedLeagues = computed(() => {
  const watched = userStore.user?.watchedLeagues
  if (!watched?.length || !leagues.value.length) return []
  return leagues.value.filter(l => watched.includes(l._id))
})

/* ── lifecycle ── */
onMounted(async () => {
  const res = await canAddEvents()
  canAddEventAllowed.value = res.allowed
  await Promise.all([fetchMonthEvents(), fetchLeagues()])
  setInterval(() => { now.value = new Date() }, 60_000)
})

watch(calendarPageId, fetchMonthEvents)

watch(monthTitle, (t) => emit('monthChange', t), { immediate: true })

watch([currentYear, currentMonth], ([y, m]) => {
  emit('month-data', { year: y, month: m })
}, { immediate: true })

watchEffect(async () => {
  if (appStore.needRefetch) await fetchMonthEvents()
})

watch([leagueUrlTitle, leagues], pickSelectedLeague, { immediate: false })
</script>

<style lang="scss" scoped>
/* ── Wrapper ── */
.ov-cal-wrapper {
  color: rgb(var(--v-theme-sheetText));
  border: none;
  border-radius: 10px;
  overflow: hidden;
  padding: 12px 12px 0;
  position: relative;
}

.ov-cal-wrapper--flat {
  border: 0 !important;
  border-radius: 0 !important;
}

/* ── Grid ── */
.ov-cal-grid {
  --gap: 6px;
  --cell-radius: 12%;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: var(--gap);
  position: relative;
}

/* Vertical divider between Friday and Saturday */
.ov-cal-grid::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(5 * (100% + var(--gap)) / 7 - var(--gap) / 2);
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(var(--v-theme-sheetText), 0.12) 10%,
    rgba(var(--v-theme-sheetText), 0.12) 90%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 1;
}

/* Horizontal divider below weekday headers */
.ov-cal-grid::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: calc(1.6em + 0px + var(--gap) / 2);
  height: 1px;
  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(var(--v-theme-sheetText), 0.12) 10%,
    rgba(var(--v-theme-sheetText), 0.12) 90%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 1;
}

.ov-weekday {
  text-align: center;
  font-weight: 700;
  font-size: clamp(10px, 1.6vw, 14px);
  padding: 6px 0;
  text-transform: capitalize;
  color: rgba(var(--v-theme-sheetText), 0.5);
  padding-bottom: 10px;
  margin-bottom: 0px;
}

/* ── Day cell ── */
.ov-day {
  background: transparent;
  border-radius: var(--cell-radius);
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  flex-direction: column;
}

.ov-day:hover {
  background: rgba(var(--v-theme-sheetText), 0.07);
}

.ov-day--today:not(.ov-day--team-single) {
  background: rgba(var(--v-theme-ice), 0.12) !important;
}

.ov-day--today {
  border: 1px solid rgba(var(--v-theme-ice), 0.4);

  .ov-day-number {
    color: rgb(var(--v-theme-ice)) !important;
    font-weight: 800;
  }
}

.ov-day--today.ov-day--team-single {
  border: 2px solid rgb(var(--v-theme-ice)) !important;
  box-shadow:
    0 0 6px 1px rgba(var(--v-theme-ice), 0.45),
    0 0 14px 3px rgba(var(--v-theme-ice), 0.25),
    0 0 24px 6px rgba(var(--v-theme-ice), 0.12);
}

.ov-day--other {
  opacity: 0.10;
  pointer-events: none;
}

.ov-day--team {
  background: rgba(var(--v-theme-sheetText), 0.03);

  &:hover {
    background: rgba(var(--v-theme-sheetText), 0.07);
  }
}

.ov-day--attending {
  background: rgba(76, 175, 80, 0.15);
  
  &:hover {
    background: rgba(76, 175, 80, 0.25);
  }
}

.ov-day--today.ov-day--attending {
  box-shadow:
    0 0 6px 1px rgba(var(--v-theme-ice), 0.45),
    0 0 14px 3px rgba(var(--v-theme-ice), 0.25),
    0 0 24px 6px rgba(var(--v-theme-ice), 0.12);
}

/* Top half: day number */
.ov-day-top {
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 2px;
}

.ov-day-top--team {
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 4% 0 0 8%;
}

.ov-day-number {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
  line-height: 1;
}

.ov-day-number--team {
  font-size: clamp(10px, 1.5vw, 16px);
  font-weight: 400;
}

.ov-day--past .ov-day-number {
  opacity: 0.45;
}

/* Bottom half: event dots */
.ov-day-bottom {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 3px;
}

.ov-day-bottom--team {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;
}

.ov-day-dots {
  display: flex;
  flex-wrap: wrap;
  gap: 0px;
  justify-content: center;
  align-items: center;
  margin-left: 2px;
}

.ov-day-dots--past {
  opacity: 0.5;
}

.ov-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: -1px;
  border: 1.5px solid rgba(var(--v-theme-sheetSection), 0.9);
  box-sizing: border-box;
}

.ov-dot-more {
  font-size: 9px;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

/* ── Month switcher ── */
.ov-month-switcher {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0px 16px 12px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.06);
}

.ov-month-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(var(--v-theme-sheetText), 0.06);
  cursor: pointer;
  color: rgba(var(--v-theme-sheetText), 0.6);
  transition: background 0.15s, color 0.15s;
}

.ov-month-nav:hover {
  background: rgba(var(--v-theme-sheetText), 0.12);
  color: rgba(var(--v-theme-sheetText), 0.85);
}

.ov-month-nav--team {
  background: rgba(var(--tc), 0.08);
  color: rgb(var(--tc));
}

.ov-month-nav--team:hover {
  background: rgba(var(--tc), 0.16);
}

.ov-month-label {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  min-width: 180px;
  text-align: center;
}

/* ── Legend ── */
.ov-cal-legend {
  padding: 0px 4px 12px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-sheetText), 0.7);
  border-top: none;

}

.ov-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  transition: opacity 0.2s ease;
}

.ov-legend-item--clickable {
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    opacity: 0.8;
  }
}

.ov-legend-item--hidden {
  opacity: 0.3;

  .ov-legend-swatch {
    background-color: rgba(var(--v-theme-sheetText), 0.2) !important;
  }
}

.ov-legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}

/* ── Day Modal ── */
.day-modal {
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 24px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  box-shadow: 
    0 8px 60px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(var(--v-theme-sheetText), 0.08);
  overflow: hidden;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.day-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.08);
}

.day-modal-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-top: 3px;
}

.day-modal-btn-placeholder {
  width: 45px;
  height: 45px;
}

.day-modal-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgba(var(--v-theme-sheetCloseBtn), 0.65);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }

  &--add {
    background: rgb(var(--v-theme-ice), 0.2);
    border: none;
    box-shadow: inset 0 0 0 2px rgb(var(--v-theme-ice));
    
    &:hover {
      background: rgb(var(--v-theme-ice), 0.3);
    }
    
    :deep(.v-icon) {
      color: rgb(var(--v-theme-ice)) !important;
    }
  }
}

.day-modal-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.day-modal-divider {
  height: 1px;
  margin: 0 16px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(var(--v-theme-sheetText), 0.12) 15%,
    rgba(var(--v-theme-sheetText), 0.12) 85%,
    transparent 100%
  );
}

.day-modal-empty {
  padding: 48px 24px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

/* ── Team mode day cells ── */
.ov-day--team-single {
  border-radius: var(--cell-radius);
}

.ov-team-single {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding-bottom: 28px;
}

.ov-team-logo-wrapper {
  width: 45%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  outline: 1px solid rgba(180, 180, 180, 0.3);
  background: rgba(200, 200, 200, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ov-team-logo {
  width: 65%;
  height: 65%;
  object-fit: contain;
}

.ov-team-label {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: clamp(10px, 1.5vw, 22px);
  font-weight: 800;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Footer with action icons (gamecenter + highlights) */
.ov-team-footer {
  width: 100%;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(4px, 1.2vw, 18px);
  pointer-events: auto;
}

.ov-team-footer--single {
  justify-content: center;
}

.ov-team-action {
  background: transparent;
  border: 0;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  cursor: pointer;
  border-radius: 8px;
  transition: filter 0.15s ease;
}

.ov-team-action:hover,
.ov-team-action:focus-visible {
  filter: brightness(1.1);
  outline: none;
}

.ov-team-divider {
  opacity: 0.8;
  user-select: none;
  font-size: clamp(14px, 1.5vw, 20px);
  pointer-events: none;
  margin: 0 1px;
}

.ov-team-multi {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  padding: 0 2px;
  align-items: stretch;
}

.ov-team-chip {
  border-radius: 4px;
  padding: 1px 3px;
  text-align: center;
  min-height: 0;
}

.ov-team-chip-text {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: clamp(7px, 1.1vw, 11px);
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.ov-team-more {
  font-size: clamp(7px, 1vw, 10px);
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-align: center;
  line-height: 1;
}

/* Dialog overlay styling */
:global(.v-dialog.backdrop .v-overlay__scrim) {
  background: rgba(0, 0, 0, 0.5) !important;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* ── Team compact mode (mobile) ── */
.ov-team-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 0;
}

.ov-team-shortcut {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: clamp(10px, 3vw, 16px);
  font-weight: 800;
  line-height: 1.2;
}

.ov-team-time {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: clamp(8px, 2.5vw, 13px);
  font-weight: 700;
  line-height: 1.2;
}

/* ── Context menu (for compact / mobile) ── */
.ctx-overlay {
  position: fixed;
  inset: 0;
  z-index: 9;
}

.ctx-menu {
  background: #161E30;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  min-width: 200px;
  padding: 4px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.6),
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 -1px 0 0 rgba(255, 255, 255, 0.06) inset;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

body:has(.v-theme--light) .ctx-menu {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.06),
    0 -1px 0 0 rgba(255, 255, 255, 0.8) inset;
}

.ctx-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  transition: background 0.15s ease, color 0.15s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.ctx-item:hover {
  background: rgba(255, 255, 255, 0.07);
  color: #ffffff;
}

.ctx-item:active {
  background: rgba(255, 255, 255, 0.04);
  transform: scale(0.98);
}

body:has(.v-theme--light) .ctx-item {
  color: rgba(0, 0, 0, 0.6);
}

body:has(.v-theme--light) .ctx-item:hover {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.85);
}

.ctx-item--bare {
  cursor: default;
}

.ctx-divider {
  height: 1px;
  margin: 4px 8px;
  background: rgba(255, 255, 255, 0.07);
}

body:has(.v-theme--light) .ctx-divider {
  background: rgba(0, 0, 0, 0.08);
}

.href-text {
  font-weight: 700;
}

/* ── Responsive: hide footer on small screens ── */
@media (max-width: 900px) {
  .ov-team-single {
    padding-bottom: 20px;
  }

  .ov-team-footer {
    gap: clamp(2px, 1vw, 10px);
  }

  .ov-team-action .v-icon {
    font-size: 18px !important;
  }
}

@media (max-width: 600px) {
  .ov-team-footer {
    display: none;
  }

  .ov-team-single {
    padding-bottom: 0;
  }

  .ov-team-logo-wrapper {
    width: 40%;
  }
}

/* ── Month slide transitions ── */
.ov-slide-right-enter-active,
.ov-slide-right-leave-active,
.ov-slide-left-enter-active,
.ov-slide-left-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ov-slide-right-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.ov-slide-right-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}
.ov-slide-left-enter-from {
  opacity: 0;
  transform: translateX(-40px);
}
.ov-slide-left-leave-to {
  opacity: 0;
  transform: translateX(40px);
}


</style>
